import { Kilogram, Pound, POUNDS_PER_GALLON_WATER, UnitsTracker } from './formulas/units.ts';
import type { HazWasteUnit } from '../types/db.ts';

export const HAZ_WASTE_MASS_UNITS: readonly HazWasteUnit[] = [
  'lbs',
  'grams',
  'milligrams',
  'kg',
  'grains',
  'ounces',
  'tons',
  'cwt',
];

export const HAZ_WASTE_VOLUME_UNITS: readonly HazWasteUnit[] = ['gallons', 'liters', 'cubicfeet', 'fluidounces'];

export const HAZ_WASTE_UNIT_OPTIONS: readonly { value: HazWasteUnit; label: string }[] = [
  { value: 'lbs', label: 'Pounds (lb)' },
  { value: 'grams', label: 'Grams (g)' },
  { value: 'milligrams', label: 'Milligrams (mg)' },
  { value: 'kg', label: 'Kilograms (kg)' },
  { value: 'grains', label: 'Grains (gr)' },
  { value: 'ounces', label: 'Ounces (oz)' },
  { value: 'tons', label: 'Short Tons (ton)' },
  { value: 'cwt', label: 'Hundredweight (cwt)' },
  { value: 'gallons', label: 'Gallons (gal)' },
  { value: 'liters', label: 'Liters (L)' },
  { value: 'cubicfeet', label: 'Cubic Feet (ft3)' },
  { value: 'fluidounces', label: 'Fluid Ounces (fl oz)' },
];

export const KG_TO_LBS = Kilogram.convertTo(Pound, 1) as number;

function convert(value: number, from: string, to: string): number | null {
  const converted = UnitsTracker.fromParsedUnitString(from).convertTo(UnitsTracker.fromParsedUnitString(to), value);
  return typeof converted === 'number' ? converted : null;
}

export function isHazWasteVolumeUnit(unit: HazWasteUnit): boolean {
  return HAZ_WASTE_VOLUME_UNITS.includes(unit);
}

export function hazWasteUnitLabel(unit: HazWasteUnit): string {
  return HAZ_WASTE_UNIT_OPTIONS.find((option) => option.value === unit)?.label ?? unit;
}

/** A waste stream's density, which is required to convert its volume units to mass. */
export interface HazWasteDensity {
  value: number;
  /** '1' = lb/gal, '2' = specific gravity. */
  unitOfMeasure: '1' | '2';
}

/** The database stores density and its unit as two columns that are null together. */
export function hazWasteStreamDensity(stream: {
  density: number | null;
  densityUnitOfMeasure: '1' | '2' | null;
}): HazWasteDensity | null {
  if (stream.density === null || stream.densityUnitOfMeasure === null) return null;
  return { value: stream.density, unitOfMeasure: stream.densityUnitOfMeasure };
}

function hazWasteDensityLbsPerGallon(density: HazWasteDensity | null): number | null {
  if (!density || density.value <= 0) return null;
  return density.unitOfMeasure === '2' ? density.value * POUNDS_PER_GALLON_WATER : density.value;
}

/** Returns null for mass units, which cannot be converted to a volume without density. */
export function hazWasteVolumeToGallons(value: number, unit: HazWasteUnit): number | null {
  if (!isHazWasteVolumeUnit(unit)) return null;
  return convert(value, unit, 'gallons');
}

export function hazWasteUnitToKg(value: number, unit: HazWasteUnit, density: HazWasteDensity | null): number | null {
  if (!isHazWasteVolumeUnit(unit)) return convert(value, unit, 'kg');
  const lbsPerGallon = hazWasteDensityLbsPerGallon(density);
  if (lbsPerGallon === null) return null;

  const gallons = hazWasteVolumeToGallons(value, unit);
  if (gallons === null) return null;
  return (gallons * lbsPerGallon) / KG_TO_LBS;
}

export function kgToHazWasteUnit(
  quantityKg: number,
  unit: HazWasteUnit,
  density: HazWasteDensity | null,
): number | null {
  if (!isHazWasteVolumeUnit(unit)) return convert(quantityKg, 'kg', unit);
  const lbsPerGallon = hazWasteDensityLbsPerGallon(density);
  if (lbsPerGallon === null) return null;

  return convert((quantityKg * KG_TO_LBS) / lbsPerGallon, 'gallons', unit);
}

/** The units the Biennial Report encodes with a GM1 UOM code. */
export type HazWasteBiennialUnit = 'lbs' | 'tons' | 'kg' | 'gallons';

/** The unit a stream's GM1, GM4, and GM5 quantities are reported in. Units without their own GM1 code are
 * normalized by dimension, so their quantities must be converted to this unit before export. */
export function hazWasteBiennialUnit(unit: HazWasteUnit): HazWasteBiennialUnit {
  if (unit === 'tons') return 'tons';
  if (unit === 'kg' || unit === 'grams' || unit === 'milligrams') return 'kg';
  if (isHazWasteVolumeUnit(unit)) return 'gallons';
  return 'lbs';
}

/** EPA GM1 UOM codes. */
export function hazWasteBiennialUnitCode(unit: HazWasteBiennialUnit): string {
  switch (unit) {
    case 'lbs':
      return '1';
    case 'tons':
      return '2';
    case 'kg':
      return '3';
    case 'gallons':
      return '5';
  }
}

/** e-Manifest UOM codes. Units without a direct code are normalized by dimension. */
export function hazWasteManifestUnitCode(unit: HazWasteUnit): string {
  if (unit === 'tons') return 'T';
  if (unit === 'kg' || unit === 'grams' || unit === 'milligrams') return 'K';
  if (unit === 'liters') return 'L';
  if (isHazWasteVolumeUnit(unit)) return 'G';
  return 'P';
}
